import { useEffect, useMemo } from "react";
import { useOpeningBalance, useTurnovers } from "api/accountingService";

interface Props {
  searchText: string;
  setSubtitle: (s: string) => void;
}

const CURRENT_YEAR = new Date().getFullYear();

const fmt = (v: number): string =>
  v || v === 0 ? v.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "";

const synOf = (code: string) => code.split("-")[0];
// Konto rozrachunkowe kontrahenta: syntetyka 20x (odbiorcy) / 21x (dostawcy) + analityka.
const isSettlementAccount = (code: string) => {
  if (!code.includes("-")) return false;
  const syn = synOf(code);
  return syn.startsWith("20") || syn.startsWith("21");
};

interface Row {
  code: string;
  name: string;
  rodzaj: string; // Odbiorcy / Dostawcy
  wn: number;
  ma: number;
  saldo: number;
}

const SettlementsRegister = ({ searchText, setSubtitle }: Props) => {
  const { data: bo, isLoading } = useOpeningBalance(CURRENT_YEAR);
  const { data: turnovers = [] } = useTurnovers();

  const rows = useMemo<Row[]>(() => {
    const turnByAcc = new Map<string, { wn: number; ma: number }>();
    for (const t of turnovers) turnByAcc.set(t.account, { wn: t.obrWn, ma: t.obrMa });

    const list: Row[] = [];
    for (const e of bo?.entries ?? []) {
      if (!isSettlementAccount(e.code)) continue;
      const tv = turnByAcc.get(e.code);
      const wn = (e.boWn || 0) + (tv?.wn ?? 0);
      const ma = (e.boMa || 0) + (tv?.ma ?? 0);
      if (wn === 0 && ma === 0) continue; // pomijamy konta bez sald i obrotów
      list.push({
        code: e.code,
        name: e.name || e.code,
        rodzaj: synOf(e.code).startsWith("20") ? "Odbiorcy" : "Dostawcy",
        wn,
        ma,
        saldo: wn - ma,
      });
    }
    return list.sort((a, b) => Math.abs(b.saldo) - Math.abs(a.saldo));
  }, [bo, turnovers]);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(q) || r.code.toLowerCase().includes(q));
  }, [rows, searchText]);

  const totals = useMemo(() => {
    let naleznosci = 0;
    let zobowiazania = 0;
    for (const r of filtered) {
      if (r.saldo > 0) naleznosci += r.saldo;
      else zobowiazania += -r.saldo;
    }
    return { naleznosci, zobowiazania, saldo: naleznosci - zobowiazania };
  }, [filtered]);

  useEffect(() => {
    setSubtitle(`Rozrachunki · ${filtered.length} kont · stan na ${CURRENT_YEAR}`);
  }, [filtered.length, setSubtitle]);

  const cell: React.CSSProperties = { padding: "6px 10px", borderBottom: "1px solid #eee", whiteSpace: "nowrap" };
  const num: React.CSSProperties = { ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums" };
  const head: React.CSSProperties = {
    padding: "8px 10px", borderBottom: "2px solid #e5e0ec", color: "#79767a",
    fontWeight: 600, fontSize: 12, position: "sticky", top: 0, background: "#fdf8fd", whiteSpace: "nowrap",
  };
  const headNum: React.CSSProperties = { ...head, textAlign: "right" };

  const tiles = [
    { label: "Należności (saldo Wn)", value: totals.naleznosci, color: "#1565c0" },
    { label: "Zobowiązania (saldo Ma)", value: totals.zobowiazania, color: "#c62828" },
    {
      label: totals.saldo >= 0 ? "Saldo netto (należności − zobowiązania)" : "Saldo netto (przewaga zobowiązań)",
      value: Math.abs(totals.saldo), color: totals.saldo >= 0 ? "#2e7d32" : "#e65100",
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", background: "#fdf8fd" }}>
      <div style={{ display: "flex", gap: 12, padding: "10px 16px 12px", flexWrap: "wrap" }}>
        {tiles.map((t) => (
          <div
            key={t.label}
            style={{
              flex: "1 1 200px", minWidth: 200,
              border: `1px solid ${t.color}33`, background: `${t.color}0d`,
              borderRadius: 8, padding: "8px 12px",
            }}
          >
            <div style={{ fontSize: 11, color: "#79767a", fontWeight: 600 }}>{t.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: t.color, marginTop: 2 }}>{fmt(t.value)} zł</div>
          </div>
        ))}
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: "0 16px 16px" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr>
              <th style={{ ...head, textAlign: "left" }}>Konto</th>
              <th style={{ ...head, textAlign: "left" }}>Kontrahent</th>
              <th style={{ ...head, textAlign: "left" }}>Rodzaj</th>
              <th style={headNum}>Winien (Wn)</th>
              <th style={headNum}>Ma</th>
              <th style={headNum}>Saldo</th>
              <th style={{ ...head, textAlign: "left" }}>Charakter</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td style={cell} colSpan={7}>Ładowanie…</td></tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td style={{ ...cell, color: "#aaa", fontStyle: "italic" }} colSpan={7}>
                  Brak rozrachunków — wczytaj bilans otwarcia (BO/ZOiS) w Ustawienia → Budżet i FK → Migracja.
                </td>
              </tr>
            ) : (
              filtered.map((r) => {
                const naleznosc = r.saldo >= 0;
                return (
                  <tr key={r.code}>
                    <td style={cell}>{r.code}</td>
                    <td style={{ ...cell, whiteSpace: "normal" }}>{r.name}</td>
                    <td style={cell}>{r.rodzaj}</td>
                    <td style={num}>{fmt(r.wn)}</td>
                    <td style={num}>{fmt(r.ma)}</td>
                    <td style={{ ...num, fontWeight: 600, color: naleznosc ? "#1565c0" : "#c62828" }}>
                      {fmt(Math.abs(r.saldo))}
                    </td>
                    <td style={{ ...cell, color: naleznosc ? "#1565c0" : "#c62828" }}>
                      {r.saldo === 0 ? "—" : naleznosc ? "Należność" : "Zobowiązanie"}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          {filtered.length > 0 && (
            <tfoot>
              <tr style={{ fontWeight: 700 }}>
                <td style={cell} colSpan={3}>RAZEM</td>
                <td style={num}>{fmt(filtered.reduce((s, r) => s + r.wn, 0))}</td>
                <td style={num}>{fmt(filtered.reduce((s, r) => s + r.ma, 0))}</td>
                <td style={num}>{fmt(totals.saldo)}</td>
                <td style={cell} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default SettlementsRegister;
